import { Button } from 'components/shadcn/button';
import { Input } from 'components/shadcn/input';
import { Label } from 'components/shadcn/label';
import { AuthContext } from 'contexts';
import { apiUrl } from 'utils/constants';
import { useContext, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useForm, SubmitHandler, Controller } from 'react-hook-form';
import { fetchWithAuth } from 'utils';
import { Role } from 'types';
import { z } from 'zod';
import { zodResolver } from '@hookform/resolvers/zod';

interface IFormInputs {
  email: string;
  password: string;
}

const NO_EMAIL = 'NO_EMAIL' as const;
const BAD_CRED = 'BAD_CRED' as const;

export const Login = () => {
  const navigate = useNavigate();
  const authContext = useContext(AuthContext);
  const { t } = useTranslation();
  const [badCredError, setBadCredError] = useState<string | null>(null);

  const validationSchema = useMemo(
    () =>
      z.object({
        email: z.string().email(t('common.invalidEmail')).min(1, t('common.required')),
        password: z.string().min(1, t('common.required')),
      }),
    [t],
  );

  const { control, handleSubmit } = useForm({
    defaultValues: {
      email: '',
      password: '',
    },
    resolver: zodResolver(validationSchema),
  });

  const onSubmit: SubmitHandler<IFormInputs> = async (data) => {
    const { email, password } = data;
    try {
      const response = await fetch(`${apiUrl}/auth/signin`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ email, password }),
      });

      const data = await response.json();
      if (response.status === 401) {
        if ('message' in data) {
          if (data.message === NO_EMAIL) {
            setBadCredError(t('auth.noEmail'));
          } else if (data.message === BAD_CRED) {
            setBadCredError(t('auth.badCred'));
          }
        }
      }
      if (data.token) {
        authContext?.login(data.token);
        const userResponse = await fetchWithAuth(`${apiUrl}/auth/user`);
        if (userResponse.ok) {
          const userResponseData = await userResponse.json();
          authContext?.saveUserId(userResponseData.id);
          authContext?.saveEmail(userResponseData.email);
          authContext?.saveRole(userResponseData.role);
          authContext?.saveCanEditDynamicPrompts(userResponseData.canEditDynamicPrompts);
          authContext?.saveCanManageRag(userResponseData.canManageRag);
          authContext?.saveCanEditDataToExtract(userResponseData.canEditDataToExtract);
          navigate(`/${userResponseData.role === Role.ADMIN ? 'admin' : 'user'}`);
        }
      }
    } catch (error) {
      console.error('Login:', error);
    }
  };

  return (
    <>
      <div className="w-full lg:grid lg:min-h-150 lg:grid-cols-2 xl:min-h-200 lg:h-svh">
        <div className="flex items-center justify-center py-12">
          <div className="mx-auto grid w-87.5 gap-6">
            <div className="grid gap-2 text-center">
              <h1 className="text-3xl font-bold text-balance">{t('auth.loginTitle')}</h1>
              <p className="text-balance text-muted-foreground">{t('auth.loginSubtitle')}</p>
            </div>
            <form autoComplete="on" onSubmit={handleSubmit(onSubmit)}>
              <div className="grid gap-4">
                <div className="grid gap-2">
                  <Controller
                    name="email"
                    control={control}
                    render={({ field: { onChange, onBlur, value }, fieldState: { error } }) => (
                      <>
                        <Label htmlFor="email">{t('common.email')}</Label>
                        <Input
                          id="email"
                          type="email"
                          placeholder={t('auth.emailPlaceholder')}
                          required
                          onChange={onChange}
                          onBlur={onBlur}
                          value={value}
                        />
                        {error && (
                          <p className="text-[0.8rem] font-medium text-destructive">
                            {error.message}
                          </p>
                        )}
                      </>
                    )}
                  />
                </div>
                <div className="grid gap-2">
                  <Controller
                    name="password"
                    control={control}
                    render={({ field: { onChange, onBlur, value }, fieldState: { error } }) => (
                      <>
                        <Label htmlFor="password">{t('common.password')}</Label>
                        <Input
                          id="password"
                          type="password"
                          required
                          onChange={onChange}
                          onBlur={onBlur}
                          value={value}
                        />
                        {error && (
                          <p className="text-[0.8rem] font-medium text-destructive">
                            {error.message}
                          </p>
                        )}
                      </>
                    )}
                  />
                </div>
                <Button type="submit" className="w-full" variant="secondary">
                  {t('common.login')}
                </Button>
              </div>
            </form>
            {badCredError && (
              <p className=" text-sm font-medium text-destructive">{badCredError}</p>
            )}
          </div>
        </div>
        <div
          className="hidden bg-muted lg:block"
          style={{
            backgroundImage: "url('/bg.png')",
            backgroundColor: '#05e292',
            backgroundSize: 'cover',
            backgroundPosition: 'right',
          }}
        ></div>
      </div>
    </>
  );
};
